import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import axiosClient from "../../axios";
import SideBar from "../SideBar";
import "../../assets/css/AdminCommon.css";
import MonthMultiSelect from "./MonthMultiSelect";
import PartnerLegend from "./PartnerLegend";
import PartnerCandleChart from "./PartnerCandleChart";
import PartnerMetricChart from "./PartnerMetricChart";
import {
	ANALYTICS_ENDPOINT,
	MAX_DATE_SPAN_DAYS,
	MAX_MONTHS,
	MAX_PARTNERS,
	METRIC_DEFS,
	PARTNERS_ENDPOINT,
	buildColorMap,
	daysBetween,
	getPartnerId,
	getPartnerName,
	normalizeMetricSeries,
	todayStr,
	currentMonthStr,
} from "./partnerAnalyticsUtils";

const MODES = [
	{ value: "date_wise", label: "Date-wise" },
	{ value: "month_wise", label: "Month-wise" },
];

const daysAgoStr = (n) => {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const PartnerAnalytics = () => {
	const [mode, setMode] = useState("date_wise");

	// Directory of all partners, for the selector options.
	const [partnerDirectory, setPartnerDirectory] = useState([]);
	const [partnerDirLoading, setPartnerDirLoading] = useState(false);

	// Date-wise controls
	const [selectedPartners, setSelectedPartners] = useState([]); // multi
	const [sDate, setSDate] = useState(daysAgoStr(29));
	const [eDate, setEDate] = useState(todayStr());

	// Month-wise controls
	const [selectedPartner, setSelectedPartner] = useState(null); // single
	const [selectedMonths, setSelectedMonths] = useState([currentMonthStr()]);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [result, setResult] = useState(null); // { mode, labels, partners, metrics }
	// Which partners are drawn on charts (date-wise can select many).
	const [visiblePartnerIds, setVisiblePartnerIds] = useState(() => new Set());

	useEffect(() => {
		setPartnerDirLoading(true);
		axiosClient
			.get(PARTNERS_ENDPOINT)
			.then((res) => {
				const list = res?.data?.response || [];
				setPartnerDirectory(Array.isArray(list) ? list : []);
			})
			.catch((err) => {
				console.log("Error fetching partners list", err);
			})
			.finally(() => setPartnerDirLoading(false));
	}, []);

	const switchMode = (nextMode) => {
		if (nextMode === mode) return;
		setError("");
		setResult(null);
		if (nextMode === "month_wise") {
			// Multi -> single: keep the first previously-selected partner, if any.
			setSelectedPartner(selectedPartners[0] || null);
		} else {
			// Single -> multi
			setSelectedPartners(selectedPartner ? [selectedPartner] : []);
		}
		setMode(nextMode);
	};

	// Mirrors backend validation so the UI can block/inform before hitting the API.
	const validationError = useMemo(() => {
		if (mode === "date_wise") {
			if (selectedPartners.length === 0) return "Select at least one partner to begin";
			if (selectedPartners.length > MAX_PARTNERS) return `Max ${MAX_PARTNERS} partners allowed`;
			if (!sDate || !eDate) return "Select a date range to begin";
			if (sDate > eDate) return "Start date must be before end date";
			if (daysBetween(sDate, eDate) > MAX_DATE_SPAN_DAYS) return `Max ${MAX_DATE_SPAN_DAYS} days allowed in date range`;
			return null;
		}
		if (!selectedPartner) return "Select a partner to begin";
		if (selectedMonths.length === 0) return "Select at least one month to begin";
		if (selectedMonths.length > MAX_MONTHS) return `Max ${MAX_MONTHS} months allowed`;
		return null;
	}, [mode, selectedPartners, sDate, eDate, selectedPartner, selectedMonths]);

	const canFetch = !validationError;

	const fetchAnalytics = () => {
		if (!canFetch) return;
		setError("");
		setLoading(true);
		setResult(null);

		const body =
			mode === "date_wise"
				? {
						mode: "date_wise",
						partner_ids: selectedPartners.map((p) => getPartnerId(p)),
						s_date: sDate,
						e_date: eDate,
				  }
				: {
						mode: "month_wise",
						partner_id: getPartnerId(selectedPartner),
						months: [...selectedMonths].sort(),
				  };

		axiosClient
			.post(ANALYTICS_ENDPOINT, body)
			.then((res) => {
				if (res?.data?.status === false) {
					setError(res?.data?.message || "Failed to fetch partner analytics");
					return;
				}
				const payload = res?.data?.response || {};
				setResult(payload);
				// Reset visibility so every partner from the response is shown.
				const ids = (payload.partners ?? []).map((p) => getPartnerId(p)).filter(Boolean);
				setVisiblePartnerIds(new Set(ids));
			})
			.catch((err) => {
				console.log("Error fetching partner analytics", err);
				setError(err?.response?.data?.message || "Failed to fetch partner analytics");
			})
			.finally(() => setLoading(false));
	};

	const labels = useMemo(() => result?.labels ?? [], [result]);
	const responsePartners = useMemo(
		() =>
			(result?.partners ?? []).map((p) => ({
				id: getPartnerId(p),
				name: getPartnerName(p),
			})),
		[result]
	);
	const colorMap = useMemo(() => buildColorMap(result?.partners ?? []), [result]);

	const metricSeriesByKey = useMemo(() => {
		if (!result?.metrics) return {};
		const map = {};
		METRIC_DEFS.forEach((def) => {
			const rawValue =
				result.metrics[def.key] ??
				def.altKeys.map((k) => result.metrics[k]).find((v) => v !== undefined);
			map[def.key] = normalizeMetricSeries(rawValue, { partners: result?.partners ?? [], labels });
		});
		return map;
	}, [result, labels]);

	return (
		<>
			<SideBar handleSideBar={true} activeTab={"partnerAnalytics"} />
			<div className="admin-screen-v4">
				{/* HEADER + SHARED CONTROLS */}
				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<div className="admin-header-v4" style={{ marginBottom: 0 }}>
						<div>
							<h2>Partner Analytics</h2>
						<p style={{ margin: "8px 0 0 0", color: "var(--text-muted)", fontSize: "14px" }}>
							{mode === "date_wise"
								? "One separate column per partner per day (colour = partner) combining Revenue + Payout + Registered Users — plus Clicks. Hover a bar for the breakdown."
								: "One bar per month combining Revenue + Payout + Registered Users — plus Clicks."}
						</p>
						</div>
					</div>

					<div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap", marginTop: "var(--spacing-xl)" }}>
						{/* Mode toggle */}
						<div>
							<div style={s.fieldLabel}>View</div>
							<div
								style={{
									display: "flex",
									background: "var(--bg-secondary, #F3F4F6)",
									border: "1px solid var(--border-color, #E5E7EB)",
									borderRadius: "8px",
									padding: "3px",
									gap: "2px",
								}}
							>
								{MODES.map((m) => (
									<button
										key={m.value}
										onClick={() => switchMode(m.value)}
										style={{
											padding: "8px 16px",
											border: "none",
											borderRadius: "6px",
											fontSize: "13px",
											fontWeight: 600,
											cursor: "pointer",
											transition: "all 0.15s",
											background: mode === m.value ? "var(--bg-card, #fff)" : "transparent",
											color: mode === m.value ? "var(--primary, #3B82F6)" : "var(--text-muted)",
											boxShadow: mode === m.value ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
										}}
									>
										{m.label}
									</button>
								))}
							</div>
						</div>

						{/* Partner selector */}
						<div>
							<div style={s.fieldLabel}>
								{mode === "date_wise" ? `Partners (multi-select, max ${MAX_PARTNERS})` : "Partner"}
							</div>
							{mode === "date_wise" ? (
								<Autocomplete
									multiple
									size="small"
									style={{ minWidth: 320 }}
									loading={partnerDirLoading}
									options={partnerDirectory}
									value={selectedPartners}
									isOptionEqualToValue={(opt, val) => getPartnerId(opt) === getPartnerId(val)}
									getOptionLabel={(opt) => getPartnerName(opt)}
									renderOption={(props, option) => (
										<li {...props} key={getPartnerId(option)}>
											{getPartnerName(option)}
											{option?.is_active === false && (
												<span style={{ marginLeft: 6, fontSize: 11, color: "#9CA3AF" }}>(inactive)</span>
											)}
										</li>
									)}
									onChange={(e, newValue) => {
										if (newValue.length > MAX_PARTNERS) return;
										setSelectedPartners(newValue);
									}}
									renderInput={(params) => <TextField {...params} placeholder="Select partners" size="small" />}
								/>
							) : (
								<Autocomplete
									size="small"
									style={{ minWidth: 260 }}
									loading={partnerDirLoading}
									options={partnerDirectory}
									value={selectedPartner}
									isOptionEqualToValue={(opt, val) => getPartnerId(opt) === getPartnerId(val)}
									getOptionLabel={(opt) => getPartnerName(opt)}
									renderOption={(props, option) => (
										<li {...props} key={getPartnerId(option)}>
											{getPartnerName(option)}
											{option?.is_active === false && (
												<span style={{ marginLeft: 6, fontSize: 11, color: "#9CA3AF" }}>(inactive)</span>
											)}
										</li>
									)}
									onChange={(e, newValue) => setSelectedPartner(newValue)}
									renderInput={(params) => <TextField {...params} placeholder="Select partner" size="small" />}
								/>
							)}
						</div>

						{/* Date range / Month(s) */}
						<div>
							<div style={s.fieldLabel}>
								{mode === "date_wise" ? `Date range (max ${MAX_DATE_SPAN_DAYS} days)` : `Month(s), max ${MAX_MONTHS}`}
							</div>
							{mode === "date_wise" ? (
								<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
									<input
										type="date"
										value={sDate || ""}
										max={eDate || todayStr()}
										onChange={(e) => setSDate(e.target.value)}
										style={s.dateInput}
									/>
									<span style={{ color: "var(--text-muted)", fontWeight: 700 }}>to</span>
									<input
										type="date"
										value={eDate || ""}
										min={sDate || undefined}
										max={todayStr()}
										onChange={(e) => setEDate(e.target.value)}
										style={s.dateInput}
									/>
								</div>
							) : (
								<MonthMultiSelect value={selectedMonths} onChange={setSelectedMonths} max={MAX_MONTHS} />
							)}
						</div>

						<button
							onClick={fetchAnalytics}
							disabled={!canFetch || loading}
							style={{
								...s.applyBtn,
								opacity: !canFetch || loading ? 0.5 : 1,
								cursor: !canFetch || loading ? "not-allowed" : "pointer",
							}}
						>
							{loading ? "Loading…" : "View Analytics"}
						</button>
					</div>

					{error && <p style={{ color: "#DC2626", marginTop: 12, marginBottom: 0 }}>{error}</p>}
				</div>

				{/* SHARED LEGEND — clickable in date-wise multi-partner mode */}
				{result && responsePartners.length > 0 && (
					<div style={{ marginBottom: "var(--spacing-xl)" }}>
						<PartnerLegend
							partners={responsePartners}
							colorMap={colorMap}
							visibleIds={visiblePartnerIds}
							onToggle={setVisiblePartnerIds}
						/>
					</div>
				)}

				{/* BODY */}
				{loading ? (
					<div className="loader-container-v4">
						<div className="loader-image-v4">📊</div>
						<p style={{ marginTop: "var(--spacing-md)", color: "var(--text-muted)" }}>Loading partner analytics...</p>
					</div>
				) : !result ? (
					<div className="empty-state-v4">
						<div className="empty-icon-v4">📈</div>
						<h3>{validationError ? "Select Filters to Begin" : "Ready to Load"}</h3>
						<p>{validationError || "Click \"View Analytics\" to load partner analytics"}</p>
					</div>
				) : (
					<>
						{/* Stacked bar — same style as UsersEarningRangePanel */}
						<PartnerCandleChart
							title="Revenue · Payout · Registered Users"
							subtitle={
								mode === "date_wise" && responsePartners.length > 1
									? "One date on x-axis — one solid-coloured column per partner, side-by-side (colour = partner). Hover a bar to see its Revenue / Payout / Registered Users breakdown."
									: "One bar per date/month. Hover a bar to see its Revenue / Payout / Registered Users breakdown."
							}
							labels={labels}
							colorMap={colorMap}
							mode={mode}
							height={340}
							visiblePartnerIds={visiblePartnerIds}
							revenueSeries={metricSeriesByKey.revenue || []}
							payoutSeries={metricSeriesByKey.payout || []}
							registeredSeries={metricSeriesByKey.registered_users || []}
						/>

						<PartnerMetricChart
							title="Clicks"
							subtitle={
								mode === "date_wise" && responsePartners.length > 1
									? "Grouped bars per partner. Hover a bar to see Earned Clicks + same-day cohort clicks/earned clicks too. Toggle partners in the legend above."
									: "Partner click volume over the selected period. Hover a bar to see Earned Clicks + same-day cohort clicks/earned clicks too."
							}
							icon="🖱️"
							labels={labels}
							colorMap={colorMap}
							mode={mode}
							visiblePartnerIds={visiblePartnerIds}
							cohortSeries={metricSeriesByKey.registered_users || []}
							metrics={[
								{
									key: "clicks",
									label: "Clicks",
									series: metricSeriesByKey.clicks || [],
									valueType: "number",
								},
								{
									key: "earned_clicks",
									label: "Earned Clicks",
									series: metricSeriesByKey.earned_clicks || [],
									valueType: "number",
									bar: false,
								},
							]}
						/>
					</>
				)}
			</div>
		</>
	);
};

const s = {
	fieldLabel: {
		fontSize: 12,
		fontWeight: 700,
		color: "var(--text-muted)",
		textTransform: "uppercase",
		letterSpacing: 0.4,
		marginBottom: 6,
	},
	dateInput: {
		padding: "8px 12px",
		border: "1px solid var(--border-color, #D1D5DB)",
		borderRadius: 8,
		fontSize: 14,
		fontWeight: 600,
		background: "var(--bg-card, #fff)",
		color: "var(--text-primary)",
		outline: "none",
		cursor: "pointer",
	},
	applyBtn: {
		padding: "10px 24px",
		background: "var(--primary, #3B82F6)",
		color: "#fff",
		border: "none",
		borderRadius: 8,
		fontSize: 14,
		fontWeight: 600,
		transition: "opacity 0.15s",
		height: 40,
	},
};

export default PartnerAnalytics;
